import { createSlice, current } from "@reduxjs/toolkit";
import {
    clearStoredAuth,
    getStoredToken,
    setStoredSession,
    getStoredOriginalToken,
    getStoredOriginalRefreshToken,
    setStoredOriginalSession,
    getStoredOriginalUserData,
    setStoredOriginalUserData,
    clearStoredImpersonation,
} from "utils/auth";

const storedToken = getStoredToken();
const storedOriginalToken = getStoredOriginalToken();
const storedOriginalUserData = getStoredOriginalUserData();

//configre auth slice-------------------
const authSlice = createSlice({
    name: "auth",
    initialState: {
        userData: {},
        isAuthenticated: Boolean(storedToken),
        hasHydrated: !storedToken,
        isImpersonating: Boolean(storedOriginalToken),
        originalToken: storedOriginalToken,
        originalRefreshToken: getStoredOriginalRefreshToken(),
        originalUserData: storedOriginalUserData,
    },
    reducers: {
        //set user date when logged in-----------
        login(state, action) {
            const data = action.payload;
            state.isAuthenticated = true;
            state.userData = data;
            state.hasHydrated = true;
            state.isImpersonating = false;
            state.originalToken = null;
            state.originalRefreshToken = null;
            state.originalUserData = null;
            setStoredSession({ token: data?.token, refreshToken: data?.refreshToken });
            clearStoredImpersonation();
        },
        // Profile hydration must never rewrite credentials. The profile API
        // returns user fields, not a new token pair; treating it as login used
        // to retain or erase tokens accidentally during boot and impersonation.
        hydrateProfile(state, action) {
            state.isAuthenticated = true;
            state.userData = action.payload;
            state.hasHydrated = true;
        },
        logout(state) {
            state.isAuthenticated = false;
            state.userData = {};
            state.hasHydrated = true;
            state.isImpersonating = false;
            state.originalToken = null;
            state.originalRefreshToken = null;
            state.originalUserData = null;
            clearStoredAuth();
        },
        markHydrated(state) {
            state.hasHydrated = true;
        },
        startImpersonation(state, action) {
            const { impersonatedUser, originalToken, originalRefreshToken } = action.payload;
            const originalUserData = current(state.userData);
            state.originalToken = originalToken;
            state.originalRefreshToken = originalRefreshToken;
            state.originalUserData = originalUserData;
            state.isImpersonating = true;
            state.userData = impersonatedUser;
            setStoredOriginalSession({ token: originalToken, refreshToken: originalRefreshToken });
            setStoredOriginalUserData(originalUserData);
            setStoredSession({
                token: impersonatedUser.token,
                refreshToken: impersonatedUser.refreshToken,
            });
        },
        stopImpersonation(state) {
            const originalToken = state.originalToken;
            const originalRefreshToken = state.originalRefreshToken;
            const originalUserData = state.originalUserData || getStoredOriginalUserData() || {};

            state.isImpersonating = false;
            state.originalToken = null;
            state.originalRefreshToken = null;
            state.originalUserData = null;
            state.userData = originalUserData;
            state.isAuthenticated = Boolean(originalToken);
            state.hasHydrated = true;

            setStoredSession({ token: originalToken, refreshToken: originalRefreshToken });
            clearStoredImpersonation();
        },
    },
});
export const authSliceActions = authSlice.actions;
export const selectIsImpersonating = (state) => state.auth.isImpersonating;

export const selectAuth = (state) => state.auth;

export default authSlice;
